import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ResponseValue } from "@/pages/deals/types";
import { useFilteredActivities } from "@/entities/activities/hooks";
import IconContainer from "@/components/ui/icon-container";
import Spinner from "@/components/ui/spinner";
import { ReferenceQuote, SquareArrowUpRight } from "@dreamhub/designsystem/icons";
import {
  useActivityTitle,
  useActivityType
} from "@/services/hooks/useActivity";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { getEntityActivityUrl } from "@/services/hooks/entities/getEntityActivityUrl";

const TRANSLATION_KEY = "deals.dealView.filledByDreamer.activity";

export type ResponseSource = Pick<
  ResponseValue,
  "activityId" | "citation" | "referenceQuote"
>;

interface Props {
  entityType: EntityType;
  entityId: string;
  response: ResponseSource;
}

const RelatedActivity: React.FC<Props> = ({
  entityType,
  entityId,
  response
}) => {
  const { t } = useTranslation();
  const activityId = response?.activityId;

  const shouldFetchActivity = Boolean(activityId);

  const {
    entities: [activityData],
    isLoading
  } = useFilteredActivities(
    entityTypeToEntityName[entityType],
    entityId || "",
    shouldFetchActivity ? { id: { eq: activityId } } : {},
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    shouldFetchActivity
  );

  const navigate = useNavigate();

  const handleOpenActivity = () => {
    navigate(getEntityActivityUrl(entityType, entityId, activityId || ""));
  };

  const isActivityDataReady =
    activityData &&
    typeof activityData === "object" &&
    activityData.hasOwnProperty("type") &&
    activityData.hasOwnProperty("notes") &&
    activityData.type !== undefined &&
    activityData.notes !== null &&
    (activityData.peopleIds === undefined ||
      Array.isArray(activityData.peopleIds));

  const activityType = useActivityType(t, activityData);
  const activityTitle = useActivityTitle(activityData);

  return isLoading || !isActivityDataReady ? (
    <div className="w-full h-full min-h-10 flex justify-center items-center">
      <Spinner size="5" />
    </div>
  ) : activityData ? (
    <div className="flex flex-col p-2 gap-2 bg-surface-focus-selected">
      <div className="flex gap-2 items-center">
        <IconContainer color="secondary">
          <ReferenceQuote />
        </IconContainer>
        <Text>{activityTitle}</Text>
        <Text variant="bodyTertiary">{`(${activityType})`}</Text>
      </div>
      {(response?.referenceQuote || response?.citation) && (
        <Text className="px-2 max-h-[14rem] overflow-y-auto italic">
          {response?.referenceQuote || response?.citation}
        </Text>
      )}
      <Button
        variant="link"
        onClick={handleOpenActivity}
        className="group flex w-fit transition-all duration-200 ease-in-out"
      >
        <IconContainer
          size="5"
          color="link"
          className="group-hover:text-link-hover transition-colors"
        >
          <SquareArrowUpRight />
        </IconContainer>
        {t(`${TRANSLATION_KEY}.link`, {
          activityType
        })}
      </Button>
    </div>
  ) : null;
};

export default RelatedActivity;
